import chalk from 'chalk';
import ora from 'ora';
import { getApiKey, getHost } from '../lib/config.js';
import { exchangeApiKey } from '../lib/api-client.js';
import { success, error, info } from '../lib/output.js';
import { isJsonMode, jsonOutput } from '../lib/globals.js';

/**
 * ENG-9486: report the mode the host is ACTUALLY in.
 *
 * This read `=== 'api_key' ? … : 'subscription'`, so an `openrouter` host —
 * inference going to a gateway, not to a subscription at all — printed
 * "subscription". The one command whose entire job is to answer "what am I
 * configured as" gave the wrong answer for a third of its own enum, confidently.
 *
 * WHY IT SURVIVED: the `--json` branch has always emitted `claude_auth_mode`
 * correctly, so every machine consumer was right and only the human-readable
 * line was wrong — nothing failed loudly enough to be looked at.
 *
 * Falls through to the RAW value rather than a friendly default. ADR-0074 takes
 * this to four credential sources, and a default that silently absorbs whatever
 * it does not recognise would reproduce this same bug against the new enum. An
 * unfamiliar mode should read as unfamiliar.
 *
 * Exported so its test exercises this function rather than a copy of it — a
 * mirrored definition in a test is the drift ENG-8231 exists to catch.
 */
export function formatClaudeAuthLabel(
  mode: string | null | undefined,
  fingerprint: string | null,
): string {
  return mode === 'api_key'
    ? `api_key (fp: ${fingerprint ?? 'unknown'})`
    : (mode ?? 'unknown');
}

export async function whoamiCommand(): Promise<void> {
  const json = isJsonMode();
  const apiKey = getApiKey();

  if (!apiKey) {
    if (json) {
      jsonOutput({ ok: false, error: 'AGT_API_KEY is not set' });
    } else {
      error('AGT_API_KEY is not set. Export it with your host API key (tlk_...)');
    }
    process.exitCode = 1;
    return;
  }

  const spinner = ora({ text: 'Exchanging API key\u2026', isSilent: json });
  spinner.start();

  try {
    const exchange = await exchangeApiKey(apiKey);
    spinner.stop();

    const framework = exchange.framework ?? 'unset';
    // ENG-9486: the RAW value, not the narrowed one.
    //
    // Review caught that exporting a better formatter was not enough on its own:
    // `attemptExchange` collapses anything that is not `api_key`/`openrouter`
    // to `subscription` BEFORE it reaches here, so the formatter's raw-value
    // branch was unreachable from this command and the forward-looking half of
    // the fix was decoration. The narrowing is correct for anything that
    // DECIDES — see api-client.ts — and wrong for anything a human reads, so
    // the two values now travel separately.
    const claudeAuthLabel = formatClaudeAuthLabel(
      exchange.claudeAuthModeRaw ?? exchange.claudeAuthMode,
      exchange.anthropicApiKeyFingerprint ?? null,
    );

    if (json) {
      jsonOutput({
        ok: true,
        api_key_prefix: apiKey.slice(0, 8) + '****',
        host: getHost(),
        host_id: exchange.hostId,
        team: exchange.teamSlug,
        team_id: exchange.teamId,
        email: exchange.userEmail,
        framework: exchange.framework,
        // Raw here too. Emitting the narrowed value while the human line shows
        // the real one would be this same inconsistency, mirrored.
        claude_auth_mode: exchange.claudeAuthModeRaw ?? exchange.claudeAuthMode,
        anthropic_api_key_fingerprint: exchange.anthropicApiKeyFingerprint,
      });
      return;
    }

    success('Authenticated via API key');
    info(`API Key:    ${chalk.bold(apiKey.slice(0, 8) + '****')}`);
    info(`Host:       ${chalk.bold(getHost())}`);
    info(`Host ID:    ${exchange.hostId}`);
    info(`Team:       ${chalk.bold(exchange.teamSlug ?? exchange.teamId)}`);
    info(`User:       ${chalk.bold(exchange.userEmail ?? 'unknown')}`);
    info(`Framework:  ${chalk.bold(framework)}`);
    if (framework === 'claude-code') {
      info(`Claude Auth: ${chalk.bold(claudeAuthLabel)}`);
    }
  } catch (err) {
    spinner.fail('Failed to exchange API key.');
    if (json) {
      jsonOutput({ ok: false, error: (err as Error).message });
    } else {
      error((err as Error).message);
    }
    process.exitCode = 1;
  }
}
